import type {
  GetPartnerIntegrationStatusFilters,
  GetPartnerIntegrationStatusPayload,
} from "../../../../../../redux/slices/integration-status/Types";
import type {
  IntegrationFilterState,
  IntegrationStageDateRange,
} from "./integration-filter-modal/integrationFilterTypes";
import { flattenIntegrationStageFilters } from "./integrationStageFilterUtils";

const joinCsv = (values: string[]): string | undefined =>
  values.length > 0 ? values.join(",") : undefined;

const buildStageDates = (
  stageDateRanges: Record<string, IntegrationStageDateRange>
): GetPartnerIntegrationStatusFilters["stage_dates"] | undefined => {
  const result: NonNullable<GetPartnerIntegrationStatusFilters["stage_dates"]> =
    {};

  for (const [stage, range] of Object.entries(stageDateRanges)) {
    const hasAny =
      range.startDateFrom ||
      range.startDateTo ||
      range.endDateFrom ||
      range.endDateTo;

    if (!hasAny) continue;

    result[stage.toLowerCase()] = {
      start_from: range.startDateFrom,
      start_to: range.startDateTo,
      end_from: range.endDateFrom,
      end_to: range.endDateTo,
    };
  }

  return Object.keys(result).length > 0 ? result : undefined;
};

const buildFilters = (
  filter: IntegrationFilterState
): GetPartnerIntegrationStatusFilters | undefined => {
  const filters: GetPartnerIntegrationStatusFilters = {};
  const flattened = flattenIntegrationStageFilters(filter);

  const partnerType = joinCsv(filter.partnerType);
  if (partnerType) filters.partner_type = partnerType;

  const region = joinCsv(filter.region);
  if (region) filters.region = region;

  const consultantId = joinCsv(filter.consultantId);
  if (consultantId) filters.consultant_id = consultantId;

  const technology = joinCsv(filter.technology);
  if (technology) filters.technology = technology;

  const integrationType = joinCsv(filter.integrationType);
  if (integrationType) filters.integration_type = integrationType;

  if (flattened.integrationStages.length > 0) {
    filters.integration_stage = joinCsv(flattened.integrationStages);

    const integrationStatus = joinCsv(flattened.integrationStatuses);
    if (integrationStatus) filters.integration_status = integrationStatus;
  }

  const validationStatus = joinCsv(flattened.validationStatuses);
  if (validationStatus) filters.validation_status = validationStatus;

  if (flattened.validationScoreValue && flattened.validationScoreOperator) {
    const parsedScore = Number(flattened.validationScoreValue);
    if (!Number.isNaN(parsedScore)) {
      filters.validation_score_operator = flattened.validationScoreOperator;
      filters.validation_score = parsedScore;
    }
  }

  const endorsementLevel = joinCsv(flattened.endorsementLevel);
  if (endorsementLevel) filters.endorsement_level = endorsementLevel;

  const stageDates = buildStageDates(flattened.stageDateRanges);
  if (stageDates) filters.stage_dates = stageDates;

  return Object.keys(filters).length > 0 ? filters : undefined;
};

export const buildIntegrationStatusRequest = (
  filter: IntegrationFilterState,
  appliedSearch: string,
  page: number,
  limit: number
): GetPartnerIntegrationStatusPayload => {
  const payload: GetPartnerIntegrationStatusPayload = {
    pagination: { page, limit },
  };

  const trimmedSearch = appliedSearch.trim();
  if (trimmedSearch) {
    payload.search = { partner_name: trimmedSearch };
  }

  const filters = buildFilters(filter);
  if (filters) {
    payload.filters = filters;
  }

  return payload;
};

export const hasDrawerFiltersInPayload = (
  payload: GetPartnerIntegrationStatusPayload
): boolean =>
  payload.filters != null && Object.keys(payload.filters).length > 0;

/**
 * Filters driven by the summary tiles / platform cards. They narrow the table
 * only, so the dashboard totals and the platform card row must stay intact —
 * otherwise selecting a card would drop every other card from the response.
 */
const DASHBOARD_NEUTRAL_FILTER_KEYS: Array<
  keyof GetPartnerIntegrationStatusFilters
> = ["validation_status", "technology"];

const isDashboardNeutralFilter = (
  filters: GetPartnerIntegrationStatusFilters
): boolean =>
  Object.keys(filters).every((key) =>
    DASHBOARD_NEUTRAL_FILTER_KEYS.includes(
      key as keyof GetPartnerIntegrationStatusFilters
    )
  );

/** Keep summary tiles / platform cards when filtering table without changing dashboard totals. */
export const shouldPreserveDashboardStats = (
  payload: GetPartnerIntegrationStatusPayload
): boolean => {
  const hasSearch = Boolean(payload.search?.partner_name);
  const filters = payload.filters;

  if (!filters || Object.keys(filters).length === 0) {
    return hasSearch;
  }

  return isDashboardNeutralFilter(filters);
};
